import _ from 'lodash';
import type ProductInterface from '~/constants/interfaces/product-interface';
import { type ContentData, type EcomProduct } from '~/constants/types/product';
import { removeTrailingZeros } from '~/util/price-formatting';

import { useConfigStore } from '~/store/config';

export type ProductType = {
  ecomData: EcomProduct;
  contentData: ContentData | null;
  algoliaObjectID?: string; // Algolia's internal objectID from search hits
};

export default class Product implements ProductInterface {
  ecomData: EcomProduct;
  contentData: ContentData | null;
  algoliaObjectID?: string;

  constructor(props: ProductType) {
    this.ecomData = props.ecomData;
    this.contentData = props.contentData;
    this.algoliaObjectID = props.algoliaObjectID;
  }

  get listType() {
    return 'product';
  }

  get title() {
    return this.ecomData.name;
  }

  get price() {
    return removeTrailingZeros(this.ecomData.price);
  }

  get priceAsNumber() {
    return this.ecomData.priceAsNumber;
  }

  get priceBeforeDiscount() {
    return removeTrailingZeros(this.ecomData.priceBeforeDiscount);
  }

  get priceBeforeDiscountAsNumber() {
    return this.ecomData.priceBeforeDiscountAsNumber;
  }

  get ecomId() {
    return Number(this.ecomData.product);
  }

  get centraVariant() {
    return this.ecomData.centraVariant;
  }

  get objectID() {
    // Return ONLY Algolia's objectID - no fallbacks
    // If there's no objectID from Algolia, return undefined
    return this.algoliaObjectID;
  }

  get itemId() {
    return this.ecomData.items[0].item;
  }

  get standardImage() {
    if (Array.isArray(this.ecomData.media && this.ecomData.media.standard)) {
      return this.ecomData.media.standard[0];
    } else {
      return 'https://placehold.co/514x567';
    }
  }

  get listingImage() {
    if (
      Array.isArray(
        this.ecomData.media &&
          this.ecomData.media.full &&
          this.ecomData.media.full[1]
      )
    ) {
      return this.ecomData.media.full[1];
    } else {
      return 'https://placehold.co/594x788';
    }
  }

  get need() {
    return this.ecomData.need ? Object.values(this.ecomData.need) : [];
  }

  get properties() {
    return this.ecomData.properties
      ? Object.values(this.ecomData.properties)
      : [];
  }

  get showAsOnSale() {
    return this.ecomData.showAsOnSale;
  }

  get available() {
    return this.ecomData.available;
  }

  get sku() {
    return this.ecomData.sku;
  }

  get category() {
    return this.ecomData.category || '';
  }

  get variant() {
    return this.ecomData.variantName;
  }

  get brand() {
    return this.ecomData.brand;
  }

  get brandName() {
    return this.ecomData.brandName;
  }

  get bundleInfo() {
    return this.ecomData.bundleInfo;
  }

  get classification() {
    return this.ecomData.classification;
  }

  get createdAt() {
    return this.ecomData.createdAt;
  }

  get excerpt() {
    return this.ecomData.excerptHtml;
  }

  get description() {
    return this.ecomData.descriptionHtml;
  }

  get modelSize() {
    return this.ecomData.var_model_size
      ? this.ecomData.var_model_size
      : this.ecomData.pr_model_size;
  }

  get priceHistory() {
    return this.ecomData.lowestPrice as any;
  }

  get measurementChart() {
    if (Array.isArray(this.ecomData.measurementChart)) {
      return null;
    } else {
      return this.ecomData.measurementChart;
    }
  }

  get gender() {
    return this.ecomData.pr_gender || [];
  }

  get type() {
    return this.ecomData.pr_type || [];
  }

  get discountPercent() {
    return this.ecomData.discountPercent;
  }

  get gtmProductValues() {
    const price = this.ecomData.priceAsNumber * 0.893;

    return {
      brand: this.ecomData.brandName,
      name: this.ecomData.name,
      category: this.ecomData.categoryUri,
      variant: this.ecomData.variantName,
      price: price.toString(),
      image: this.standardImage,
    };
  }

  get productImages() {
    return this.ecomData?.media?.standard || [];
  }

  get productImagesFullsize() {
    return this.ecomData.media.full || [];
  }

  get productImagesVeryLargesize() {
    return this.ecomData.media.verylarge || [];
  }

  get mainImage() {
    return this.productImages.length ? this.productImages[0] : null;
  }

  get items() {
    return this.ecomData.items || [];
  }

  get color() {
    return this.ecomData.var_sh_swatch || {};
  }

  get isNews() {
    return JSON.stringify(this.ecomData.categories).indexOf('Nyheter')>0;
  }

  get isReturnable() {
    return (!this.ecomData.pr_not_returnable_value || this.ecomData.pr_not_returnable_value === '0');
  }

  get isChannelWebshop() {
    return (this.ecomData.var_channel.includes('Webshop'));
  }

  get isArchived() {
    return (this.ecomData.var_archived_value && !!Number(this.ecomData.var_archived_value));
  }

  get hideSizeChoice() {
    return (this.ecomData.pr_hide_size_value && !!Number(this.ecomData.pr_hide_size_value));
  }

  get hideColorChoice() {
    return (this.ecomData.pr_hide_color_value && !!Number(this.ecomData.pr_hide_color_value));
  }

  get ArchivedMessage() {
    return this.ecomData.var_archived_message_text;
  }

  get material() {
    return (this.ecomData.pr_material && this.ecomData.pr_material[0]) || '';
  }

  get materialList() {
    return this.ecomData.pr_material || [];
  }

  get variantName() {
    return this.ecomData.variantName;
  }

  get videoUrl() {
    return this.ecomData.video_url_text;
  }

  get washing() {
    return this.ecomData.pr_washing;
  }

  composition(lang: string): string | null {
    switch (lang) {
      case 'no':
        return this.ecomData.pr_composition_no_text;
      case 'fi':
        return this.ecomData.pr_composition_fi_text;
      case 'se':
        return this.ecomData.pr_composition_se_text;
    }
    return null;
  }

  get seal() {
    return this.ecomData.pr_seal;
  }

  get hasVariants() {
    return this.variants.length;
  }

  get variants() {
    const variants =
      this.ecomData.relatedProducts
        ?.filter((r) => r.relation === 'variant')
        ?.map((p) => new Product({ ecomData: p } as ProductType)) ?? [];

    variants.push(this);
    return _.sortBy(variants, 'ecomId');
  }

  get hasMatchWithProducts() {
    return this.matchWithProducts.length;
  }

  get matchWithProducts() {
    return (
      this.ecomData.relatedProducts
        ?.filter((r) => r.relation === 'Match with')
        ?.map((p) => new Product({ ecomData: p } as ProductType)) ?? []
    );
  }

  get hasRelatedProducts() {
    return this.relatedProducts.length;
  }

  get relatedProducts() {
    return (
      this.ecomData.relatedProducts
        ?.filter((r) => r.relation !== 'variant' && r.relation !== 'Match with')
        ?.map((p) => new Product({ ecomData: p } as ProductType)) ?? []
    );
  }
  get allowStockMonitoring(): boolean {
    return JSON.stringify(this.ecomData.categories).indexOf('BackInStockEnabled')>0;
  }
  get hasFlags() {
    return this.flags.length;
  }

  get flags(): { type: string, translate: boolean, value: string }[] {
    const flags = [];

    if (this.showAsOnSale) {
      flags.push({
        type: 'discount',
        translate: !this.discountPercent,
        value: this.discountPercent ? `-${this.discountPercent}%` : 'discount',
      });
    }
    if (this.isNews) {
      flags.push({
        type: 'news',
        translate: true,
        value: 'news',
      });
    }
    if (Array.isArray(this.materialList)) {
      this.materialList.forEach((element) => {
        flags.push({
          type: 'material',
          translate: false,
          value: element,
        });
      });
    }

    return flags;
  }

  get route() {
    const config = useConfigStore();
    return config.getProductRoute(this.ecomId)?.route || '/';
  }
}
